import React, { Fragment, isValidElement, useId, useState } from "react"
import type { FC, MouseEvent, ReactElement, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { HStack } from "@/nzds/layout"

import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { IconButton, Menu, MenuItem, Typography } from "@mui/material"

import type { PageTitleProps } from "./page-title.types"

const flattenElements = (node: ReactNode): ReactElement[] => {
  if (node == null || typeof node === "boolean") {
    return []
  }

  if (Array.isArray(node)) {
    return node.flatMap((child: ReactNode) => flattenElements(child))
  }

  if (!isValidElement(node)) {
    return []
  }

  if (node.type === Fragment) {
    return flattenElements((node.props as { children?: ReactNode }).children)
  }

  return [node]
}

const getElementKey = (
  element: ReactElement,
  index: number,
  fallbackPrefix: string
): string | number => {
  if (element.key != null) {
    return element.key
  }

  const props = element.props as { href?: unknown; to?: unknown }
  const destination = props.to ?? props.href
  if (typeof destination === "string" || typeof destination === "number") {
    return destination
  }

  return `${fallbackPrefix}-${index}`
}

const BreadcrumbActionsMenu: FC<{ links: ReactElement[] }> = ({ links }) => {
  const { t } = useTranslation("common")
  const menuId = useId()
  const buttonId = useId()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const isMenuOpen = Boolean(menuAnchorEl)

  return (
    <>
      <IconButton
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-expanded={isMenuOpen ? "true" : undefined}
        aria-haspopup="true"
        aria-label={t("pageTitle.breadcrumbActionsMenu")}
        color="neutral"
        id={buttonId}
        onClick={(event) => {
          setMenuAnchorEl(event.currentTarget)
        }}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        id={menuId}
        onClose={() => {
          setMenuAnchorEl(null)
        }}
        open={isMenuOpen}
        slotProps={{
          list: {
            "aria-labelledby": buttonId,
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        {links.map((link, index) => {
          const {
            children: linkChildren,
            component,
            href,
            onClick,
            reloadDocument,
            replace,
            state,
            to,
          } = link.props as {
            children?: ReactNode
            component?: React.ElementType
            href?: string
            onClick?: (event: MouseEvent<HTMLElement>) => void
            reloadDocument?: boolean
            replace?: boolean
            state?: unknown
            to?: string
          }

          return (
            <MenuItem
              key={getElementKey(link, index, "breadcrumb")}
              // Navigation props only — omit Link styling (underline, color, etc.)
              // so they are not forwarded to the DOM via MenuItem.
              {...({
                component,
                href,
                reloadDocument,
                replace,
                state,
                to,
              } as React.ComponentProps<typeof MenuItem>)}
              onClick={(event: MouseEvent<HTMLElement>) => {
                onClick?.(event)
                setMenuAnchorEl(null)
              }}
            >
              {linkChildren}
            </MenuItem>
          )
        })}
      </Menu>
      <ChevronRightRoundedIcon aria-hidden color="action" fontSize="small" />
    </>
  )
}

const SecondaryActionsMenu: FC<{ actions: ReactElement[] }> = ({ actions }) => {
  const { t } = useTranslation("common")
  const menuId = useId()
  const buttonId = useId()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const isMenuOpen = Boolean(menuAnchorEl)

  return (
    <>
      <IconButton
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-expanded={isMenuOpen ? "true" : undefined}
        aria-haspopup="true"
        aria-label={t("pageTitle.secondaryActionsMenu")}
        color="neutral"
        id={buttonId}
        onClick={(event) => {
          setMenuAnchorEl(event.currentTarget)
        }}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        id={menuId}
        onClose={() => {
          setMenuAnchorEl(null)
        }}
        open={isMenuOpen}
        slotProps={{
          list: {
            "aria-labelledby": buttonId,
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        {actions.map((action, index) => {
          const {
            children: actionChildren,
            color,
            disabled,
            onClick,
          } = action.props as {
            children?: ReactNode
            color?: string
            disabled?: boolean
            onClick?: (event: MouseEvent<HTMLElement>) => void
          }

          return (
            <MenuItem
              disabled={disabled}
              key={getElementKey(action, index, "secondary-action")}
              onClick={(event: MouseEvent<HTMLElement>) => {
                onClick?.(event)
                setMenuAnchorEl(null)
              }}
              sx={color === "error" ? { color: "error.main" } : undefined}
            >
              {actionChildren}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

/**
 * In-content page title with Polaris-style breadcrumb menu and actions.
 *
 * Layout matches Shopify Polaris Page:
 * `⋯ breadcrumbs` > heading … actions `⋯ secondary` primaryActions
 * https://shopify.dev/docs/api/app-home/web-components/layout-and-structure/page
 */
export const PageTitle: FC<PageTitleProps> = ({
  actions,
  breadcrumbActions,
  heading,
  primaryActions,
  secondaryActions,
}) => {
  const breadcrumbLinks = breadcrumbActions
    ? flattenElements(breadcrumbActions)
    : []
  const secondaryActionItems = secondaryActions
    ? flattenElements(secondaryActions)
    : []
  const hasBreadcrumbs = breadcrumbLinks.length > 0
  const hasSecondaryActions = secondaryActionItems.length > 0
  const hasVisibleActions =
    Boolean(actions) || hasSecondaryActions || Boolean(primaryActions)

  return (
    <HStack
      sx={{
        alignItems: "center",
        gap: 2,
        justifyContent: "space-between",
      }}
    >
      <HStack sx={{ alignItems: "center", gap: 0.5, minWidth: 0 }}>
        {hasBreadcrumbs ? (
          <BreadcrumbActionsMenu links={breadcrumbLinks} />
        ) : null}
        <Typography
          component="h2"
          noWrap
          sx={{ lineHeight: "40px" }}
          variant="h2"
        >
          {heading}
        </Typography>
      </HStack>
      {hasVisibleActions ? (
        <HStack sx={{ alignItems: "center", flexShrink: 0, gap: 1 }}>
          {actions}
          {hasSecondaryActions ? (
            <SecondaryActionsMenu actions={secondaryActionItems} />
          ) : null}
          {primaryActions}
        </HStack>
      ) : null}
    </HStack>
  )
}
